/**
 * @file 埋点上报会员信息相关字段工具
 * @description 对外只有两个入口：
 * ·点击埋点 / SDK config —— `useMemberLogExt()` 拿到字段直接 spread
 * ·曝光埋点 —— `useMemberExtShowLog(params, send?)`，参数 falsy 表示未曝光
 *
 * 维度由上下文自适应（项目页上报项目制片人的会员，个人页上报当前用户），
 * 业务方无需选择；会员信息的异步就绪时序与「同一次曝光不重复上报」也都在内部处理。
 *
 * 约定：**一次 `useMemberExtShowLog` 调用 = 一个曝光身份固定的曝光位**。
 * 工具只判断「此刻该不该曝光」，不判断「在曝光什么」——
 * 若同一处 UI 会原地切换曝光对象（如共用弹窗的多个 Tab），
 * 请按身份拆成多次调用，各自持有独立的显示条件，不要靠改 params 复用同一次调用。
 */

import {useEffect, useMemo, useRef} from 'react';
import {useAuthStore} from '@/store/useAuthStore';
import {useUserBenefitProfile, useProjectBenefitProfile} from '@/commerce/benefit';
import {useUrlProjectId} from '@/hooks/useUrlProjectId';
import {useTracking} from '@/services/tracking/context';
import type {LogExt, LogParams} from '@/services/tracking';

type ShowLogParams = Omit<LogParams, 'type'>;

const EMPTY_EXT: LogExt = {};

interface MemberLogExtState {
    ext: LogExt;
    /** 会员信息是否已确定（可信） */
    resolved: boolean;
}

/**
 * 上下文自适应会员埋点字段 + 就绪状态
 * @description 项目上下文取项目会员（由项目制片人的订阅决定），否则取当前用户
 */
const useMemberLogExtState = (): MemberLogExtState => {
    const initialized = useAuthStore((s) => s.initialized);
    const isLogin = useAuthStore((s) => s.isLogin);
    const projectId = useUrlProjectId();
    const userProfile = useUserBenefitProfile();
    const projectProfile = useProjectBenefitProfile();

    const profile = projectId ? projectProfile : userProfile;

    const enabled = isLogin;
    const memberInfo = enabled ? profile.data?.memberInfo : undefined;
    const memberLevel = memberInfo?.memberLevel;
    const subscriptionType = memberInfo?.subscriptionType;

    const ext = useMemo<LogExt>(() => {
        if (memberLevel === undefined || subscriptionType === undefined) {
            return EMPTY_EXT;
        }

        /* eslint-disable camelcase */
        return {
            member_level: memberLevel,
            subscription_type: subscriptionType,
        };
        /* eslint-enable camelcase */
    }, [memberLevel, subscriptionType]);

    // ·登录态未知不可信
    // ·query 未启用（未登录 / 无 projectId）时可信，无会员相关信息
    // ·query 启用时权益接口请求落定（isSuccess || isError）后可信
    const resolved = initialized && (!enabled || profile.isSuccess || profile.isError);

    return {ext, resolved};
};

/**
 * 会员埋点字段
 * @description 用于点击埋点、SDK config 等「发送时机由用户行为决定」的场景，直接 spread 进 ext。
 * 曝光埋点请用 `useMemberExtShowLog`，不要自己在 effect 里 spread 本 hook 的返回值（订阅/升级会员刷新会员信息可能造成重复上报打点）。
 */
export const useMemberLogExt = (): LogExt => useMemberLogExtState().ext;

/**
 * 会员曝光埋点（需要上报会员字段的展现埋点统一使用此 hook）
 * @param params 埋点参数，falsy（未打开 / 参数未就绪）表示当前不该曝光
 * @param send 自定义发送通道，默认 `useTracking().sendShowLog`。
 * 若所在模块有 sender wrapper（如 useWorkspaceTracking / useMembershipTracking），必须传入，
 * 否则会丢掉 wrapper 注入的字段（role / scene_key 等）
 * @description 仅在 params 由 falsy 变为非空、且会员信息已就绪的那一刻上报一条。
 *
 * 一次调用只负责一个曝光身份固定的曝光位（见文件头约定）：
 * 曝光对象会原地切换时请拆成多次调用，而非在同一次调用里改 params。
 */
export const useMemberExtShowLog = (
    params: ShowLogParams | null | undefined | false,
    send?: (p: ShowLogParams) => void,
): void => {
    const {sendShowLog} = useTracking();
    const {ext, resolved} = useMemberLogExtState();
    const shouldLog = !!params && resolved;
    const sentRef = useRef(false);

    useEffect(() => {
        if (!shouldLog) {
            sentRef.current = false;
            return;
        }
        if (sentRef.current) {
            return;
        }

        sentRef.current = true;
        (send ?? sendShowLog)({...params, ext: {...params.ext, ...ext}});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldLog]);
};
